import {
  type MeDto,
  type ProtocolTemplateDto,
  TRADE_KINDS,
  type TaskType,
  type TradeDto,
  type TradeKind,
  WORK_STAGES,
  type WorkDto,
  workProtocolKind, CASE_DOCUMENT_CATEGORY_LABELS } from '@bqcrm/shared';
import { Accordion, Alert, Button, Card, ColorInput, Grid, Group, Modal, NavLink, Select, Stack, Switch, Tabs, Text, TextInput, MultiSelect } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconInfoCircle, IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { ask } from '../confirm';
import { useSearchParams } from 'react-router';
import { api } from '../api';
import { TradeBadge } from '../components/Badges';
import { PageHeader } from '../components/PageHeader';
import { QueryState } from '../components/QueryState';
import { SectionCard } from '../components/ui';
import { useCompanies, useTrades } from '../data';
import { notifyError, notifyOk } from '../notify';
import { ProtocolEditor, WorkEditor } from './settings/WorkEditors';
import { TradeFunctionHolders } from '../components/process/UserFunctions';

const GENERAL = '*';

// ── dane branży ────────────────────────────────────────────────────────────

function TradeSettings({ trade }: { trade: TradeDto }) {
  const companies = useCompanies();
  const [f, setF] = useState({ name: trade.name, kind: trade.kind, color: trade.color, defaultCompanyId: trade.defaultCompanyId, active: trade.active, requiredDocuments: trade.requiredDocuments ?? [] });
  useEffect(() => setF({ name: trade.name, kind: trade.kind, color: trade.color, defaultCompanyId: trade.defaultCompanyId, active: trade.active, requiredDocuments: trade.requiredDocuments ?? [] }), [trade]);
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: () => api(`/dict/trades/${trade.code}`, { method: 'PUT', body: f }),
    onSuccess: () => {
      notifyOk('Zapisano branżę');
      void qc.invalidateQueries({ queryKey: ['trades'] });
    },
    onError: notifyError,
  });
  return (
    <SectionCard>
      <Stack gap="xs" maw={720}>
        <TextInput label="Nazwa" value={f.name} onChange={(e) => setF({ ...f, name: e.currentTarget.value })} />
        <Select
          label="Rodzaj zakresu"
          data={Object.entries(TRADE_KINDS).map(([value, label]) => ({ value, label }))}
          value={f.kind}
          onChange={(v) => v && setF({ ...f, kind: v as TradeKind })}
          allowDeselect={false}
        />
        <Select
          label="Domyślna firma realizująca"
          description="Podpowiadana w nowym zapytaniu; można ją zmienić w zakresie sprawy."
          data={(companies.data ?? []).map((c) => ({ value: c.id, label: c.shortName }))}
          value={f.defaultCompanyId}
          onChange={(v) => setF({ ...f, defaultCompanyId: v })}
          clearable
        />
        <ColorInput label="Kolor" value={f.color} onChange={(v) => setF({ ...f, color: v })} format="hex" />
        <MultiSelect
          label="Wymagane dokumenty w sprawie"
          description="Kategorie dokumentów, które muszą być w sprawie z tą branżą przed zamknięciem (np. dokumentacja powykonawcza, zdjęcia)."
          data={Object.entries(CASE_DOCUMENT_CATEGORY_LABELS).map(([value, label]) => ({ value, label }))}
          value={f.requiredDocuments}
          onChange={(v) => setF({ ...f, requiredDocuments: v })}
          placeholder={f.requiredDocuments.length ? undefined : 'bez wymagań'}
        />
        <Switch label="Aktywna" checked={f.active} onChange={(e) => setF({ ...f, active: e.currentTarget.checked })} />
        <Group justify="flex-end">
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Zapisz
          </Button>
        </Group>
      </Stack>
    </SectionCard>
  );
}

// ── protokół pracy ─────────────────────────────────────────────────────────

/** Protokół, który ekipa podpisze z klientem po tej pracy: wzór branży albo ogólny (z możliwością własnego). */
function WorkProtocol({ taskType, tradeCode, tradeName, protocols, onProtocols }: { taskType: TaskType; tradeCode: string; tradeName: string; protocols: ProtocolTemplateDto[]; onProtocols: (l: ProtocolTemplateDto[]) => void }) {
  const kind = workProtocolKind(taskType);
  const [open, setOpen] = useState(false);
  const phone = useMediaQuery('(max-width: 48em)');
  // niezapisane zmiany w edytorze — okno nie zamyka się po cichu
  const dirty = useRef(false);
  const close = async () => {
    if (dirty.current && !(await ask('Zmiany we wzorze protokołu nie zostały zapisane i zostaną utracone.', { title: 'Zamknąć bez zapisywania?', confirm: 'Zamknij' }))) return;
    dirty.current = false;
    setOpen(false);
  };
  const create = useMutation({
    mutationFn: () => api<ProtocolTemplateDto[]>('/settings/protocol-templates', { method: 'POST', body: { kind, tradeCode } }),
    onSuccess: (l) => {
      onProtocols(l);
      setOpen(true);
      notifyOk('Utworzono własny wzór protokołu (kopia ogólnego)');
    },
    onError: notifyError,
  });
  if (!kind) return <Text size="xs" c="dimmed">Ta praca kończy się bez protokołu.</Text>;
  const own = protocols.find((p) => p.kind === kind && p.tradeCode === tradeCode);
  const tpl = own ?? protocols.find((p) => p.kind === kind && p.tradeCode === GENERAL);
  return (
    <Card withBorder padding="sm" bg="var(--mantine-color-default-hover)">
      <Group justify="space-between" wrap="wrap" gap="xs">
        <div>
          <Text size="sm" fw={600}>
            Protokół: {tpl?.definition.title ?? '—'}
          </Text>
          <Text size="xs" c="dimmed">
            {own || tradeCode === GENERAL ? `wersja ${tpl?.version ?? 1}` : 'wzór ogólny — ta branża nie ma własnego'}
            {tpl?.signedCount ? ` · podpisane protokoły (${tpl.signedCount}) zachowują swoją wersję` : ''}
          </Text>
        </div>
        {own || tradeCode === GENERAL ? (
          <Button size="xs" variant="light" onClick={() => setOpen(true)} disabled={!tpl}>
            Edytuj protokół
          </Button>
        ) : (
          <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} loading={create.isPending} onClick={() => create.mutate()}>
            Własny protokół dla branży
          </Button>
        )}
      </Group>
      {tpl && (
        <Modal
          opened={open}
          onClose={() => void close()}
          size="xl"
          fullScreen={phone}
          title={`Wzór protokołu: ${tpl.kindLabel} — ${tpl.tradeCode === GENERAL ? 'wzór ogólny (branże bez własnego)' : tradeName}`}
        >
          <ProtocolEditor tpl={tpl} onSaved={onProtocols} onDirtyChange={(d) => (dirty.current = d)} />
        </Modal>
      )}
    </Card>
  );
}

// ── realizacja branży / listy ogólne ─────────────────────────────────────────

function useWorks() {
  const qc = useQueryClient();
  const works = useQuery({ queryKey: ['settings-works'], queryFn: () => api<WorkDto[]>('/settings/works') });
  const protocols = useQuery({ queryKey: ['settings-protocols'], queryFn: () => api<ProtocolTemplateDto[]>('/settings/protocol-templates') });
  return {
    works,
    protocols,
    onWorks: (l: WorkDto[]) => qc.setQueryData(['settings-works'], l),
    onProtocols: (l: ProtocolTemplateDto[]) => qc.setQueryData(['settings-protocols'], l),
  };
}

/** Realizacja branży: czynności ekipy (w kolejności wykonywania) i protokół odbioru. */
function RealizationTab({ tradeCode, tradeName }: { tradeCode: string; tradeName: string }) {
  const { works, protocols, onWorks, onProtocols } = useWorks();
  const all = works.data ?? [];
  const own = all.find((w) => w.tradeCode === tradeCode && w.taskType === 'montaz');
  const general = all.find((w) => w.tradeCode === GENERAL && w.taskType === 'montaz');
  const work: WorkDto | undefined = own ?? (general ? { ...general, tradeCode, custom: false, updatedAt: null, updatedByName: null } : undefined);
  return (
    <QueryState isLoading={works.isLoading || protocols.isLoading} error={works.error ?? protocols.error}>
      <Stack gap="md">
        <Alert variant="light" color="gray" icon={<IconInfoCircle />}>
          Branża decyduje, co ekipa robi przy realizacji i co jest w protokole odbioru. Wspólne zadanie kilku branż dostaje czynności każdej z nich (pogrupowane) i wspólny protokół. Zmiany dotyczą nowych zadań — utworzone zachowują swoją listę.
        </Alert>
        {work && (
          <SectionCard title="Czynności realizacji">
            <Text size="xs" c="dimmed" mb="sm">
              W kolejności wykonywania — razem z uruchomieniem i przekazaniem klientowi.
            </Text>
            <WorkEditor work={work} isNew={!own} onSaved={onWorks} />
          </SectionCard>
        )}
        <WorkProtocol taskType="montaz" tradeCode={tradeCode} tradeName={tradeName} protocols={protocols.data ?? []} onProtocols={onProtocols} />
        <TradeServiceWorks tradeCode={tradeCode} all={all} onWorks={onWorks} />
      </Stack>
    </QueryState>
  );
}

/** Czynności przeglądu i serwisu branży (QA5 P30) — bez własnej listy branża korzysta z listy ogólnej. */
function TradeServiceWorks({ tradeCode, all, onWorks }: { tradeCode: string; all: WorkDto[]; onWorks: (l: WorkDto[]) => void }) {
  return (
    <>
      {(['przeglad', 'serwis'] as const).map((type) => {
        const own = all.find((w) => w.tradeCode === tradeCode && w.taskType === type);
        const general = all.find((w) => w.tradeCode === GENERAL && w.taskType === type);
        const work: WorkDto | undefined = own ?? (general ? { ...general, tradeCode, custom: false, updatedAt: null, updatedByName: null } : undefined);
        if (!work) return null;
        return (
          <SectionCard key={type} title={`Czynności: ${WORK_STAGES[type].label.toLowerCase()}`}>
            <Text size="xs" c="dimmed" mb="sm">
              {own ? 'Lista tej branży — dla zgłoszeń serwisowych jej instalacji.' : 'Branża korzysta z listy ogólnej („Wszystkie branże”). Zapis utworzy jej własną listę, np. z pomiarami właściwymi dla branży.'}
            </Text>
            <WorkEditor work={work} isNew={!own} onSaved={onWorks} />
          </SectionCard>
        );
      })}
    </>
  );
}

/** Listy ogólne: wizja, pomiary, serwis, przegląd, inne — i zapasowa lista realizacji (branże bez własnej). */
function GeneralTab() {
  const { works, protocols, onWorks, onProtocols } = useWorks();
  const [open, setOpen] = useState<string | null>(null);
  const list = (works.data ?? []).filter((w) => w.tradeCode === GENERAL);
  return (
    <QueryState isLoading={works.isLoading || protocols.isLoading} error={works.error ?? protocols.error}>
      <Stack gap="md">
        <Alert variant="light" color="gray" icon={<IconInfoCircle />}>
          Listy wspólne dla wszystkich branż. „Realizacja” jest zapasowa — dla branż bez własnej listy realizacji.
        </Alert>
        <Accordion variant="separated" value={open} onChange={setOpen}>
          {list.map((w) => (
            <Accordion.Item key={w.taskType} value={w.taskType}>
              <Accordion.Control>
                <Group gap="sm" wrap="wrap">
                  <Text fw={600}>{WORK_STAGES[w.taskType].label}</Text>
                  <Text size="xs" c="dimmed">
                    {WORK_STAGES[w.taskType].hint} · {w.items.length} czynności{w.minPhotos ? ` · min. ${w.minPhotos} zdj.` : ''}
                    {!w.custom ? ' · lista wbudowana' : ''}
                  </Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <WorkEditor work={w} onSaved={onWorks} />
                  <WorkProtocol taskType={w.taskType} tradeCode={GENERAL} tradeName="wszystkie branże" protocols={protocols.data ?? []} onProtocols={onProtocols} />
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </Stack>
    </QueryState>
  );
}

// ── nowa branża ────────────────────────────────────────────────────────────

function NewTradeModal({ onClose, onCreated }: { onClose: () => void; onCreated: (code: string) => void }) {
  const companies = useCompanies();
  const qc = useQueryClient();
  const [f, setF] = useState({ code: '', name: '', kind: 'instalacja' as TradeKind, color: '#1971c2', defaultCompanyId: null as string | null });
  const create = useMutation({
    mutationFn: () => api<TradeDto>('/dict/trades', { method: 'POST', body: f }),
    onSuccess: (t) => {
      notifyOk(`Dodano branżę „${t.name}”`);
      void qc.invalidateQueries({ queryKey: ['trades'] });
      onCreated(t.code);
    },
    onError: notifyError,
  });
  const codeOk = /^[A-Z0-9]{2,4}$/.test(f.code);
  return (
    <Modal opened onClose={onClose} title="Nowa branża">
      <Stack gap="xs">
        <TextInput
          label="Kod"
          description="2–4 wielkie litery lub cyfry, np. GR — skrót widoczny na znaczkach; później się nie zmienia"
          value={f.code}
          maxLength={4}
          onChange={(e) => setF({ ...f, code: e.currentTarget.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
          error={f.code && !codeOk ? 'Kod: 2–4 znaki' : undefined}
        />
        <TextInput label="Nazwa" placeholder="np. Gruntowe wymienniki ciepła" value={f.name} onChange={(e) => setF({ ...f, name: e.currentTarget.value })} />
        <Select label="Rodzaj zakresu" data={Object.entries(TRADE_KINDS).map(([value, label]) => ({ value, label }))} value={f.kind} onChange={(v) => v && setF({ ...f, kind: v as TradeKind })} allowDeselect={false} />
        <Select label="Domyślna firma realizująca" data={(companies.data ?? []).map((c) => ({ value: c.id, label: c.shortName }))} value={f.defaultCompanyId} onChange={(v) => setF({ ...f, defaultCompanyId: v })} clearable />
        <ColorInput label="Kolor" value={f.color} onChange={(v) => setF({ ...f, color: v })} format="hex" />
        <Text size="xs" c="dimmed">
          Po dodaniu ustaw czynności realizacji i protokół (zakładka „Realizacja”) oraz kroki procesu. Do czasu ustawienia branża korzysta z list ogólnych.
        </Text>
        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button disabled={!codeOk || f.name.trim().length < 2} loading={create.isPending} onClick={() => create.mutate()}>
            Dodaj branżę
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ── strona ─────────────────────────────────────────────────────────────────

/** Ustawienia → Branże: realizacja branży (czynności, protokół odbioru), funkcje i osoby, dane; listy ogólne. */
export function ProcessSettingsPage(_: { me: MeDto }) {
  const trades = useTrades();
  const [params, setParams] = useSearchParams();
  const selected = params.get('branza') ?? trades.data?.[0]?.code ?? null;
  // dawna zakładka „Kroki procesu” (usunięta) — realizacja
  const tab = params.get('zakladka')?.replace(/^kroki$/, 'realizacja') ?? 'realizacja';
  const go = (p: Record<string, string>) => setParams({ branza: selected ?? '', zakladka: tab, ...p }, { replace: true });
  const trade = trades.data?.find((t) => t.code === selected);
  const [adding, setAdding] = useState(false);

  return (
    <>
      <PageHeader
        title="Branże i procesy"
        description="Branża decyduje o realizacji: co ekipa robi (czynności w kolejności) i co jest w protokole odbioru. Planując realizację, wybierasz branże sprawy."
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={() => setAdding(true)}>
            Nowa branża
          </Button>
        }
      />
      <QueryState isLoading={trades.isLoading} error={trades.error}>
        <Grid>
          <Grid.Col span={{ base: 12, md: 3 }}>
            {trades.data?.map((t) => (
              <NavLink
                key={t.code}
                active={t.code === selected}
                label={t.name}
                leftSection={<TradeBadge code={t.code} trade={t} />}
                description={t.active ? undefined : 'nieaktywna'}
                onClick={() => go({ branza: t.code, zakladka: 'realizacja' })}
              />
            ))}
            <NavLink active={selected === GENERAL} label="Wszystkie branże" description="wizja, pomiary, serwis, przegląd" onClick={() => go({ branza: GENERAL, zakladka: 'realizacja' })} mt="xs" />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 9 }}>
            {selected === GENERAL ? (
              <GeneralTab />
            ) : (
              trade && (
                <Tabs value={tab} onChange={(v) => go({ zakladka: v ?? 'realizacja' })} keepMounted={false}>
                  <Tabs.List mb="md">
                    <Tabs.Tab value="realizacja">Realizacja</Tabs.Tab>
                    <Tabs.Tab value="osoby">Funkcje i osoby</Tabs.Tab>
                    <Tabs.Tab value="dane">Dane branży</Tabs.Tab>
                  </Tabs.List>
                  <Tabs.Panel value="realizacja">
                    <RealizationTab tradeCode={trade.code} tradeName={trade.name} />
                  </Tabs.Panel>
                  <Tabs.Panel value="osoby">
                    <Stack gap="xs" maw={720}>
                      <Text size="sm" c="dimmed">
                        Kto pełni funkcje w tej branży — do tych osób trafiają zadania (oferty, umowy, planowanie, rozliczenia) spraw z tą branżą. Kolejkę zadań daje rola (Ustawienia → Role), a branże ustawiasz w „Pracownicy → karta pracownika → Dostęp”.
                      </Text>
                      <TradeFunctionHolders code={trade.code} />
                    </Stack>
                  </Tabs.Panel>
                  <Tabs.Panel value="dane">
                    <TradeSettings trade={trade} />
                  </Tabs.Panel>
                </Tabs>
              )
            )}
          </Grid.Col>
        </Grid>
      </QueryState>
      {adding && (
        <NewTradeModal
          onClose={() => setAdding(false)}
          onCreated={(code) => {
            setAdding(false);
            go({ branza: code, zakladka: 'realizacja' });
          }}
        />
      )}
    </>
  );
}
